import { useCallback, useMemo, useSyncExternalStore } from 'react'
import {
  createHttpClient, createTrainingApiClient, useGuidedTours,
  type GuidedTours, type TrainingApiClient,
} from '@userhub/widget'
import { getUserhubToken } from '../lib/auth'
import { APP_VERSION } from '../lib/version'
import { TOUR_DEFS } from '../tours/defs'
import type { TourEvent, TourVerdicts } from '../tours/TourRunner'
import type { TourDef } from '../tours/types'
import { USERHUB_API_BASE_URL } from './config'

/**
 * The guided tours' records in userhub (completions, 👍/👎) — userhub plan G3/G4. Niobe's backend
 * records nothing.
 *
 * One training client for the whole app, built once: `useGuidedTours(client)` keys its store on
 * the client object, so the tours window, the engine's completion record and the catalog dialog
 * (`<GuidedToursCatalog apiClient>`) all share one store, loaded once. It wraps the widget's own
 * client only to know whether userhub is answering: the like buttons show only then. A down or
 * slow userhub never surfaces — the widget falls back to "nothing completed" and only warns.
 */
let reachable = false
const listeners = new Set<() => void>()

function setReachable(v: boolean): void {
  if (v === reachable) return
  reachable = v
  for (const l of listeners) l()
}

function tracked<T>(p: Promise<T>): Promise<T> {
  return p.then(
    (v) => { setReachable(true); return v },
    (e: unknown) => { setReachable(false); throw e },
  )
}

let client: TrainingApiClient | null = null

/** The app's one training client (lazily built: a module import must not touch the widget). */
export function trainingClient(): TrainingApiClient {
  if (client) return client
  const inner = createTrainingApiClient(
    createHttpClient({ baseUrl: USERHUB_API_BASE_URL, getAccessToken: getUserhubToken }),
    APP_VERSION,
  )
  client = {
    getMyTours: (app) => tracked(inner.getMyTours(app)),
    recordCompletion: (input) => tracked(inner.recordCompletion(input)),
    setLiked: (input) => tracked(inner.setLiked(input)),
    // The 👍 counts are the catalog's business; they say nothing about the user's own records.
    getStats: (app) => inner.getStats(app),
  }
  return client
}

/** Tests only: forget the client and the reachability (each spec starts with a fresh store). */
export function resetTrainingForTests(): void {
  client = null
  reachable = false
}

const subscribe = (cb: () => void): (() => void) => {
  listeners.add(cb)
  return () => listeners.delete(cb)
}
const snapshot = (): boolean => reachable

export interface TourRecords extends GuidedTours {
  /** userhub answered the last records call (load, completion, verdict): the like buttons may show. */
  reachable: boolean
}

/** The user's tour records + whether userhub is answering. */
export function useTourRecords(): TourRecords {
  const tours = useGuidedTours(trainingClient())
  const ok = useSyncExternalStore(subscribe, snapshot, snapshot)
  return { ...tours, reachable: ok }
}

/**
 * What the tour engine (TourProvider in App.tsx) needs from userhub: its `completed` event records
 * the completion (fire-and-forget: the store updates at once, the PUT follows); the
 * congratulations card's 👍 / 👎 set the verdict, which the store queues behind that completion.
 * The buttons only show once userhub has answered and the tour has a record (userhub G4).
 */
export function useTourRecording(defs: readonly TourDef[] = TOUR_DEFS): {
  onEvent: (e: TourEvent) => void
  verdicts: TourVerdicts
} {
  const { recordCompletion, setLiked, reachable, records } = useTourRecords()
  const onEvent = useCallback((e: TourEvent) => {
    if (e.type !== 'completed') return
    const def = defs.find((d) => d.id === e.tourId)
    if (def) recordCompletion(def.number, def.version)
  }, [defs, recordCompletion])
  const verdicts = useMemo<TourVerdicts>(() => ({
    verdictOf: (n) => {
      const r = reachable ? records?.[n] : undefined
      return r ? (r.liked ?? null) : undefined
    },
    setVerdict: (n, liked) => setLiked(n, liked),
  }), [reachable, records, setLiked])
  return { onEvent, verdicts }
}
